import { useState, useEffect, useRef } from 'react';
import { Link, Outlet, useNavigate } from '@tanstack/react-router';
import { Menu, X, Layers, Video, FolderOpen, Target, PenLine, MessageSquare, PanelLeftOpen, HelpCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { LessonHeader } from './LessonHeader';
import { LessonRail } from './LessonRail';
import { flattenTopics, adjacentTopics, findCurrentModule } from './lessonNav';

import { useTopicDetail, useBatchCurriculum, useCompleteTopic } from '@/lib/hooks/useLms';

const RAIL_KEY = 'kp-lesson-rail-collapsed';

interface TopicShellProps {
  batchId: string;
  topicId: string;
}

interface TabLink {
  to: string;
  label: string;
  Icon: LucideIcon;
  show: boolean;
}

/** Shared chrome for every topic tab route: header, tab nav (real links, one per
 *  route), curriculum rail/drawer, and an <Outlet/> for the active tab's page.
 *  Each tab is its own route (not client-side tab state) so it's deep-linkable
 *  and shareable — see routes/_student/courses/$batchId/topics/$topicId/*. */
export function TopicShell({ batchId, topicId }: TopicShellProps) {
  const navigate = useNavigate();
  const { data: topic, isLoading, isError, refetch } = useTopicDetail(topicId);
  const { data: curriculum } = useBatchCurriculum(batchId);
  const complete = useCompleteTopic();

  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(RAIL_KEY) === '1'; } catch { return false; }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  const drawerPanelRef = useRef<HTMLDivElement>(null);
  const drawerCloseButtonRef = useRef<HTMLButtonElement>(null);
  const curriculumTriggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (drawerOpen) drawerCloseButtonRef.current?.focus();
    else curriculumTriggerRef.current?.focus();
  }, [drawerOpen]);

  useEffect(() => {
    if (!drawerOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setDrawerOpen(false); return; }
      if (e.key !== 'Tab') return;
      const panel = drawerPanelRef.current;
      if (!panel) return;
      const focusable = Array.from(
        panel.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, [tabindex]:not([tabindex="-1"])'),
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [drawerOpen]);

  const toggleRail = () =>
    setRailCollapsed((c) => {
      const n = !c;
      try { localStorage.setItem(RAIL_KEY, n ? '1' : '0'); } catch { /* ignore */ }
      return n;
    });

  if (isLoading) return <TopicShellSkeleton />;

  if (isError || !topic) {
    return (
      <div className="mx-auto max-w-2xl p-6 text-center">
        <p className="text-[#6b7280]">Couldn&apos;t load this lesson.</p>
        <button onClick={() => refetch()} className="mt-3 rounded-lg bg-[#ff5a1f] px-4 py-2 text-sm font-semibold text-white">Retry</button>
      </div>
    );
  }

  const flat = flattenTopics(curriculum?.modules);
  const { next } = adjacentTopics(flat, topicId);
  const currentModule = findCurrentModule(curriculum?.modules, topicId);
  const isCompleted = currentModule?.topics.find((t) => t.id === topicId)?.status === 'completed';

  const goNext = () => {
    if (next) navigate({ to: '/courses/$batchId/topics/$topicId', params: { batchId, topicId: next.id } });
  };
  const onMarkComplete = async () => {
    if (isCompleted) return;
    try { await complete.mutateAsync(topicId); } catch { /* surfaced by mutation state */ }
  };
  const onCompleteNext = async () => {
    if (isCompleted) { goNext(); return; }
    try { await complete.mutateAsync(topicId); goNext(); } catch { /* surfaced by mutation state */ }
  };
  const onPlay = () => {
    const to = topic.sessions.length > 0
      ? '/courses/$batchId/topics/$topicId/recording'
      : '/courses/$batchId/topics/$topicId';
    navigate({ to, params: { batchId, topicId } });
  };

  const tabLinks: TabLink[] = [
    { to: '/courses/$batchId/topics/$topicId', label: 'Overview', Icon: Layers, show: true },
    { to: '/courses/$batchId/topics/$topicId/recording', label: 'Recording', Icon: Video, show: topic.sessions.length > 0 },
    { to: '/courses/$batchId/topics/$topicId/resources', label: 'Resources', Icon: FolderOpen, show: topic.resources.length > 0 },
    { to: '/courses/$batchId/topics/$topicId/practice', label: 'Practice', Icon: Target, show: topic.assessments.length > 0 },
    { to: '/courses/$batchId/topics/$topicId/notes', label: 'Notes', Icon: PenLine, show: true },
    { to: '/courses/$batchId/topics/$topicId/ask-trainer', label: 'Ask Trainer', Icon: HelpCircle, show: true },
    { to: '/courses/$batchId/topics/$topicId/feedback', label: 'Feedback', Icon: MessageSquare, show: true },
  ];

  return (
    <div className="flex min-h-full flex-col bg-[#fafafa]">
      <LessonHeader
        batchId={batchId} batchName={curriculum?.batch_name ?? 'Batch'}
        moduleTitle={topic.module_title} title={topic.title} topicType={topic.topic_type}
        subtitle={topic.subtitle}
        complexityTime={topic.complexity_time} complexitySpace={topic.complexity_space}
        estimatedHours={topic.estimated_hours}
        completedTopics={curriculum?.completed_topics ?? 0} totalTopics={curriculum?.total_topics ?? 0}
        isCompleted={!!isCompleted} completing={complete.isPending} hasNext={!!next}
        onPlay={onPlay} onMarkComplete={onMarkComplete} onCompleteNext={onCompleteNext}
      />

      <div className="mx-auto flex w-full max-w-7xl flex-1 gap-6 px-4 py-6 sm:px-6">
        <main className="min-w-0 flex-1">
          <nav className="mb-6 flex h-auto w-full justify-start gap-1 overflow-x-auto border-b border-[#ececec]" aria-label="Topic sections">
            {tabLinks.filter((t) => t.show).map((t) => (
              <Link
                key={t.to}
                to={t.to}
                params={{ batchId, topicId }}
                activeOptions={{ exact: true }}
                className="inline-flex items-center gap-2 whitespace-nowrap border-b-2 border-transparent px-4 py-3 text-sm font-semibold text-[#9ca3af] transition-colors hover:text-[#6b7280] [&.active]:border-[#ff5a1f] [&.active]:text-[#ff5a1f]"
                activeProps={{ className: 'active' }}
              >
                <t.Icon className="size-4" />
                {t.label}
              </Link>
            ))}
          </nav>

          <Outlet />

          <button ref={curriculumTriggerRef} onClick={() => setDrawerOpen(true)}
            className="mt-6 inline-flex items-center gap-2 rounded-xl border border-[#e5e7eb] px-3 py-2 text-sm font-medium text-[#6b7280] lg:hidden">
            <Menu className="size-4" /> Topic Navigation
          </button>
        </main>

        {!railCollapsed ? (
          <div className="hidden w-[320px] flex-shrink-0 lg:block">
            <div className="sticky top-6">
              <LessonRail module={currentModule} batchId={batchId} currentTopicId={topicId} onCollapse={toggleRail} />
            </div>
          </div>
        ) : (
          <div className="hidden flex-shrink-0 lg:block">
            <button onClick={toggleRail} aria-label="Show topic navigation"
              className="sticky top-6 flex items-center gap-2 rounded-xl border border-[#e5e7eb] bg-white px-3 py-2.5 text-sm font-medium text-[#6b7280] transition-colors hover:bg-[#f9fafb]">
              <PanelLeftOpen className="size-4" />
            </button>
          </div>
        )}
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden" role="dialog" aria-modal="true" aria-label="Topic Navigation">
          <div className="flex-1 bg-black/40" onClick={() => setDrawerOpen(false)} />
          <div ref={drawerPanelRef} className="w-[85%] max-w-xs overflow-y-auto bg-[#fafafa] p-4">
            <button ref={drawerCloseButtonRef} onClick={() => setDrawerOpen(false)} aria-label="Close topic navigation" className="mb-3 inline-flex rounded-lg border border-[#e5e7eb] p-1.5 text-[#6b7280]">
              <X className="size-4" />
            </button>
            <LessonRail module={currentModule} batchId={batchId} currentTopicId={topicId} onCollapse={() => setDrawerOpen(false)} />
          </div>
        </div>
      )}
    </div>
  );
}

function TopicShellSkeleton() {
  return (
    <div className="mx-auto max-w-7xl p-4 sm:p-6">
      <div className="mb-5 h-8 w-2/3 animate-pulse rounded-md bg-[#ececec]" />
      <div className="aspect-video w-full animate-pulse rounded-xl bg-[#ececec]" />
      <div className="mt-4 space-y-3">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-20 animate-pulse rounded-xl bg-[#ececec]" />)}</div>
    </div>
  );
}
